/*
 * Copyright 2021 Google LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     https://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

/**
 * External dependencies
 */
import {
  FontService,
  type CustomFontData,
  type Page,
} from '@src/web-stories-wp/story-editor/src/elements/types';
import type { Options } from 'html-to-image/lib/types';

/**
 * Internal dependencies
 */
import { hasElementBackdrop } from '../../../elements/shared/getElementBackdropStyles';
import storyPageToNode from './storyPageToNode';
import { waitForLutProcessing } from './lutPreview';
import { flattenLinearLightForCapture } from '../../../utils/linearLight/capture';
import { getLinearLightFontEmbedCSS } from '../../../utils/linearLight/fonts';
import elementIs from '../../../elements/utils/elementIs';
import { collectTextFontUsage } from '../../../elements/text/utils/mixedTypography';

/**
 * @typedef {import('@googleforcreators/elements').Page} Page
 */

/**
 * Async method to generate a dataUrl from a story page.
 */
interface StoryPageToCanvasOptions extends Options {
  width?: number;
  contexts?: Parameters<typeof storyPageToNode>[2]['contexts'];
}

async function storyPageToCanvas(
  page: Page,
  { width = 400, contexts, ...options }: StoryPageToCanvasOptions,
  getFontCss: (
    fontData: CustomFontData,
    cacheKey: string
  ) => Promise<string | null>
) {
  const htmlToImage = await import('html-to-image');

  const [node, cleanup] = await storyPageToNode(page, width, {
    renderFullHeightThumb: true,
    contexts,
  });

  try {
    await waitForLutProcessing(node);

    const customFontsToLoad = new Map<
      string,
      { font: CustomFontData; cacheKey: string }
    >();

    for (const element of page.elements) {
      if (!elementIs.text(element)) {
        continue;
      }

      for (const usage of collectTextFontUsage(element)) {
        if (usage.font.service !== FontService.Custom) {
          continue;
        }
        const key = [
          usage.identityKey,
          usage.revision || '',
          usage.renderFamily,
        ].join('|');
        if (!customFontsToLoad.has(key)) {
          customFontsToLoad.set(key, {
            cacheKey: key,
            font: {
              ...usage.font,
              family: usage.renderFamily,
            },
          });
        }
      }
    }

    let fontEmbedCSS = page.elements.some(
      (element) => element.blendMode === 'linear-light'
    )
      ? await getLinearLightFontEmbedCSS(node)
      : await htmlToImage.getFontEmbedCSS(node);

    for (const { font, cacheKey } of Array.from(customFontsToLoad.values())) {
      // for custom fonts, the CSS generated by getFontEmbedCSS doesn't work—probably because it can't download the font files
      // so we need to generate our own CSS and override the html-to-image one for these fonts
      const fontCss = await getFontCss(font, cacheKey);

      if (fontCss) {
        fontEmbedCSS += fontCss;
      }
    }

    await flattenLinearLightForCapture(node, { fontEmbedCSS, ...options });

    const canvas = await htmlToImage.toCanvas(node, {
      fontEmbedCSS,
      ...options,
    });

    cleanup();

    return canvas;
  } catch (error) {
    cleanup();
    if (
      page.elements.some(
        (element) =>
          element.blendMode === 'linear-light' || hasElementBackdrop(element)
      )
    )
      throw error;
  }
}

export default storyPageToCanvas;
